import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import axiosInstance from '../../lib/axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Clock, Video, Edit, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime12h } from '../../lib/utils';

export default function AppointmentsManagement() {
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [meetLinkLoadingId, setMeetLinkLoadingId] = useState(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const res = await axiosInstance.get('/appointments');
      setAppointments(res.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axiosInstance.patch(`/appointments/${id}/status?status=${status}`);
      toast.success('Status updated');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getMeetLink = async (id) => {
    setMeetLinkLoadingId(id);
    try {
      const res = await axiosInstance.post(`/appointments/${id}/meet-link`);
      setAppointments((prev) =>
        prev.map((apt) => (apt.id === id ? { ...apt, google_meet_link: res.data.google_meet_link } : apt))
      );
      window.open(res.data.google_meet_link, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get Meet link');
    } finally {
      setMeetLinkLoadingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'completed': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-100';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const filteredAppointments = filter === 'all' 
    ? appointments 
    : appointments.filter(apt => apt.status === filter);

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-manrope font-bold mb-2">Appointments</h1>
            <p className="text-slate-600">Manage all appointments</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Appointments</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4">
          {filteredAppointments.map((apt, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getStatusColor(apt.status)}>{apt.status}</Badge>
                      {apt.consultation_type === 'video' && <Video className="h-4 w-4 text-primary" />}
                    </div>
                    <p className="font-medium">Patient: {apt.patient_email}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {apt.appointment_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTime12h(apt.appointment_time)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {apt.id ? (
                      <>
                        {apt.status === 'pending' && (
                          <Button size="sm" onClick={() => updateStatus(apt.id, 'confirmed')}>
                            Confirm
                          </Button>
                        )}
                        {apt.status === 'confirmed' && apt.consultation_type === 'video' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={meetLinkLoadingId === apt.id}
                            onClick={() =>
                              apt.google_meet_link ? window.open(apt.google_meet_link, '_blank') : getMeetLink(apt.id)
                            }
                          >
                            {meetLinkLoadingId === apt.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Video className="h-4 w-4 mr-2" />
                            )}
                            {apt.google_meet_link ? 'Join Meet' : 'Get Meet Link'}
                          </Button>
                        )}
                        {apt.status === 'confirmed' && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(apt.id, 'completed')}>
                            Complete
                          </Button>
                        )}
                        {apt.status !== 'cancelled' && (
                          <Button size="sm" variant="destructive" onClick={() => updateStatus(apt.id, 'cancelled')}>
                            Cancel
                          </Button>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-amber-600">Legacy appointment (no actions)</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredAppointments.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-center text-slate-500">
                No appointments found
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}